import React, { useCallback, useEffect, useState } from 'react'
import { object } from 'prop-types'
import { useFormikContext } from 'formik'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash } from '@fortawesome/pro-regular-svg-icons/faTrash'
import { toast } from 'react-toastify'
import * as yup from 'yup'
import { CREATE, DELETE, GET_LIST, UPDATE, useClient } from '@peracto/client'
import { Checkbox, Form, Group, Input } from '@peracto/peracto-ui'

import { FITTING_POSITION, linkForApi, linkLimit, linkSource, parseCriteriaValues } from './categoryFields'
import { showApiError } from './apiErrors'

const EMPTY_LINK = {
    genericArticleId: '',
    genericArticleName: '',
    limited: false,
    criteriaId: FITTING_POSITION,
    criteriaValues: '',
}

const linkSchema = yup.object().shape({
    genericArticleId: yup
        .number()
        .typeError('Enter the TecDoc GenArt number, e.g. 62.')
        .integer()
        .positive('Enter the TecDoc GenArt number, e.g. 62.')
        .required('Enter the TecDoc GenArt number, e.g. 62.'),
    criteriaId: yup.number().when('limited', {
        is: true,
        then: yup
            .number()
            .typeError('Enter the criterion number, e.g. 100 for Fitting Position.')
            .integer()
            .positive()
            .required('Enter the criterion number, e.g. 100 for Fitting Position.'),
    }),
    criteriaValues: yup.string().when('limited', {
        is: true,
        then: yup.string().required('Enter at least one value, e.g. 83, 259, 260.'),
    }),
})

const LimitFields = () => {
    const { values } = useFormikContext()

    if (!values.limited) {
        return null
    }

    return (
        <>
            <Input
                name="criteriaId"
                type="number"
                label="Criterion"
                help="TecDoc's criterion number: 100 is Fitting Position."
                required
            />
            <Input
                name="criteriaValues"
                label="Values"
                help="TecDoc's value keys, separated by commas, e.g. 83, 259, 260 for Exterior Mirror, Left Exterior Mirror and Right Exterior Mirror."
                required
            />
        </>
    )
}

const CategoryGenericArticles = ({ category }) => {
    const { client } = useClient()
    const [links, setLinks] = useState(null)

    const load = useCallback(async () => {
        const { data } = await client(GET_LIST, 'vehicle-category-generic-articles', {
            filter: { category: category['@id'], pagination: false },
        })
        setLinks(data)
    }, [client, category])

    useEffect(() => {
        load()
    }, [load])

    const onAdd = async (values, actions) => {
        try {
            await client(CREATE, 'vehicle-category-generic-articles', {
                data: {
                    category: category['@id'],
                    genericArticleId: parseInt(values.genericArticleId, 10),
                    genericArticleName: values.genericArticleName.trim() || null,
                    criteriaId: values.limited ? parseInt(values.criteriaId, 10) : null,
                    criteriaValues: values.limited ? parseCriteriaValues(values.criteriaValues) : [],
                },
            })
            toast.success(`GenArt ${values.genericArticleId} added.`)
            actions.resetForm({ values: EMPTY_LINK })
            actions.setSubmitting(false)
            load()
        } catch (e) {
            showApiError(e, actions)
        }
    }

    const onReviewed = async link => {
        try {
            await client(UPDATE, 'vehicle-category-generic-articles', {
                id: link['@id'],
                data: { ...linkForApi(link), needsReview: false },
            })
            toast.success(`GenArt ${link.genericArticleId} marked as reviewed.`)
            load()
        } catch (e) {
            showApiError(e)
        }
    }

    const onDelete = async link => {
        try {
            await client(DELETE, 'vehicle-category-generic-articles', { id: link['@id'] })
            toast.success(`GenArt ${link.genericArticleId} removed.`)
            load()
        } catch (e) {
            showApiError(e)
        }
    }

    return (
        <div className="mt-4" id="generic-articles">
            <h3>GenArts</h3>
            <p className="text-muted">
                The TecDoc part types (GenArts) this category lists. Limit one to a criterion to list only
                some of its parts here, e.g. GenArt 62 (direction indicators) limited to the exterior mirror
                fitting positions for a Door Mirrors category, while Lighting lists all of them. GenArts
                loaded from Somora&apos;s specification show the TecDoc node they came from; one marked Check
                needs a person to confirm it belongs here (its note says why), then Mark reviewed, or remove it.
            </p>
            <div className="card mb-3">
                <div className="card-body">
                    {links === null ? (
                        'Loading...'
                    ) : links.length === 0 ? (
                        <p className="mb-0">No GenArts yet, so this category shows no parts.</p>
                    ) : (
                        <table className="table table-sm mb-0">
                            <thead>
                                <tr>
                                    <th>GenArt</th>
                                    <th>From TecDoc node</th>
                                    <th>Parts listed</th>
                                    <th>Review</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {links.map(link => (
                                    <tr
                                        key={link['@id']}
                                        data-testid={`generic-article-${link.genericArticleId}`}
                                    >
                                        <td>
                                            {link.genericArticleId}
                                            {link.genericArticleName && (
                                                <div className="small">{link.genericArticleName}</div>
                                            )}
                                        </td>
                                        <td className="text-muted">{linkSource(link)}</td>
                                        <td>{linkLimit(link)}</td>
                                        <td style={{ whiteSpace: 'pre-wrap' }}>
                                            {link.needsReview && (
                                                <>
                                                    <span className="badge badge-pill badge-warning mr-2">
                                                        Check
                                                    </span>
                                                    <button
                                                        type="button"
                                                        className="btn btn-link p-0"
                                                        onClick={() => onReviewed(link)}
                                                        data-testid={`reviewed-${link.genericArticleId}`}
                                                    >
                                                        Mark reviewed
                                                    </button>
                                                </>
                                            )}
                                            {link.reviewNote && (
                                                <div className="small text-muted">{link.reviewNote}</div>
                                            )}
                                        </td>
                                        <td className="text-right">
                                            <button
                                                type="button"
                                                className="btn btn-link text-danger p-0"
                                                onClick={() => onDelete(link)}
                                                aria-label={`Remove GenArt ${link.genericArticleId}`}
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
            <Form
                values={EMPTY_LINK}
                schema={linkSchema}
                onSubmit={onAdd}
                showActionPanel={false}
                autoComplete="off"
            >
                <Group name="Add a GenArt" id="add-generic-article">
                    <Input name="genericArticleId" type="number" label="GenArt" required />
                    <Input
                        name="genericArticleName"
                        label="GenArt name"
                        help="TecDoc's name for it, e.g. Sensor, wheel speed, so others can see what it is."
                    />
                    <Checkbox name="limited" label="Only parts with certain criterion values" />
                    <LimitFields />
                    <button type="submit" className="btn btn-primary">
                        Add GenArt
                    </button>
                </Group>
            </Form>
        </div>
    )
}

CategoryGenericArticles.propTypes = {
    category: object.isRequired,
}

export default CategoryGenericArticles
